import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, AlertCircle } from "lucide-react";
import { useMemo } from "react";

interface Profile {
  description: string;
  skills: string[];
  photoUrl: string | null;
  full_name: string | null;
  headline: string | null;
  location: string | null;
  experience_level: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  portfolio_url: string | null;
  instagram_url: string | null;
}

interface ProfileProgressProps {
  profile: Profile;
  onEditClick: () => void;
}

interface FieldCheck {
  key: string;
  label: string;
  completed: boolean;
  priority: "high" | "medium" | "low";
}

export function ProfileProgress({ profile, onEditClick }: ProfileProgressProps) {
  const { completedFields, totalFields, percentage, missingFields } = useMemo(() => {
    const fields: FieldCheck[] = [
      {
        key: "full_name",
        label: "Nome completo",
        completed: !!profile.full_name?.trim(),
        priority: "high",
      },
      {
        key: "photoUrl",
        label: "Foto de perfil",
        completed: !!profile.photoUrl,
        priority: "high",
      },
      {
        key: "headline",
        label: "Título profissional",
        completed: !!profile.headline?.trim(),
        priority: "high",
      },
      {
        key: "location",
        label: "Localização",
        completed: !!profile.location?.trim(),
        priority: "medium",
      },
      {
        key: "description",
        label: "Biografia",
        completed: !!profile.description?.trim(),
        priority: "high",
      },
      {
        key: "experience_level",
        label: "Nível de experiência",
        completed: !!profile.experience_level,
        priority: "medium",
      },
      {
        key: "skills",
        label: "Habilidades (mín. 3)",
        completed: profile.skills?.length >= 3,
        priority: "high",
      },
      {
        key: "linkedin_url",
        label: "LinkedIn",
        completed: !!profile.linkedin_url?.trim(),
        priority: "medium",
      },
    ];

    const completed = fields.filter((f) => f.completed).length;
    const total = fields.length;
    const pct = Math.round((completed / total) * 100);
    const missing = fields.filter((f) => !f.completed);

    return {
      completedFields: completed,
      totalFields: total,
      percentage: pct,
      missingFields: missing,
    };
  }, [profile]);

  // Don't show if profile is complete
  if (percentage === 100) {
    return null;
  }

  const getProgressColor = () => {
    if (percentage < 40) return "bg-red-500";
    if (percentage < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStatusMessage = () => {
    if (percentage < 40) return "Complete seu perfil para aparecer no ranking!";
    if (percentage < 70) return "Bom progresso! Faltam poucos campos.";
    return "Quase lá! Finalize seu perfil.";
  };

  return (
    <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-4 mb-4 border border-primary/20">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="h-4 w-4 text-primary" />
            <h4 className="font-medium text-sm">Completude do Perfil</h4>
          </div>
          <p className="text-xs text-muted-foreground">{getStatusMessage()}</p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-primary">{percentage}%</span>
        </div>
      </div>

      <Progress
        value={percentage}
        className="h-2 mb-3"
      />

      <div className="flex flex-wrap gap-2 mb-3">
        {missingFields.slice(0, 4).map((field) => (
          <span
            key={field.key}
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${
              field.priority === "high"
                ? "bg-destructive/15 text-destructive border-destructive/40 dark:bg-destructive/20 dark:text-red-200"
                : "bg-amber-500/15 text-amber-800 border-amber-600/40 dark:bg-amber-500/20 dark:text-amber-200 dark:border-amber-400/40"
            }`}
          >
            <Circle className="h-2 w-2 fill-current" />
            {field.label}
          </span>
        ))}
        {missingFields.length > 4 && (
          <span className="text-xs text-muted-foreground">
            +{missingFields.length - 4} mais
          </span>
        )}
      </div>

      <Button
        onClick={onEditClick}
        size="sm"
        className="w-full sm:w-auto"
      >
        Completar Perfil (+25 pts)
      </Button>
    </div>
  );
}
